/**
 * useUnreadNotifications Hook
 * Manages unread notification count for mobile navigation
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getNotifications } from '../services/notificationsApi';

export const useUnreadNotifications = () => {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    try {
      const result = await getNotifications({ limit: 1 });
      if (result.success && result.data) {
        // API returns: { success: true, data: [...], pagination: {...} }
        // Get pagination from either root level or nested in data
        const pagination = (result as any).pagination || (result.data as any).pagination || {};
        setUnreadCount(pagination.unreadCount ?? 0);
      } else {
        setUnreadCount(0);
      }
    } catch (error) {
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Poll for new notifications every 3 minutes
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const pollInterval = setInterval(() => {
      fetchUnreadCount();
    }, 180000); // 3 minutes

    return () => clearInterval(pollInterval);
  }, [fetchUnreadCount, isAuthenticated]);

  return { unreadCount, loading, refresh: fetchUnreadCount };
};

export default useUnreadNotifications;
